import React, { useState } from 'react';
import { Screen, AppState } from '../App';
import { User, getStudentById, saveStudent, generateId, Win, getStudentStrategies, StrategyAttempt, saveStrategyAttempt, getStrategyScore, getDailyRatings, getTodayDateKey, saveDailyRating, DailyRating as DailyRatingEntry } from '../storage';
import { getSubBrief, streamBrainScience, getStrategies, streamForYou, streamReferral, getMTSSAssessment } from '../api';
import { roleLabels } from '../roleLabels';
import AiDisclaimer from '../components/AiDisclaimer';
import NavBar from '../components/NavBar';

interface Props { navigate: (s: Screen, state?: AppState) => void; user: User | null; appState: AppState; }

export default function StudentProfile({ navigate, user, appState }: Props) {
  const L = roleLabels(user);
  const [showWin, setShowWin] = useState(false);
  const [ratingKey, setRatingKey] = useState(0);
  const [winText, setWinText] = useState('');
  const [winSaved, setWinSaved] = useState(false);
  const [expandedConv, setExpandedConv] = useState<string | null>(null);
  const [expandedTab, setExpandedTab] = useState<Record<string, string>>({});
  const [showSubBrief, setShowSubBrief] = useState(false);
  const [subBrief, setSubBrief] = useState('');
  const [subLoading, setSubLoading] = useState(false);
  const [triedStrategies, setTriedStrategies] = useState<string[]>([]);
  // Regenerate a section that wasn't completed before the student was saved.
  const [regen, setRegen] = useState<{ conv: string; section: string } | null>(null);
  const [regenErr, setRegenErr] = useState<{ conv: string; section: string } | null>(null);
  const [regenStream, setRegenStream] = useState('');
  const [, setTick] = useState(0);

  const student = user && appState.currentStudentId ? getStudentById(user.id, appState.currentStudentId) : null;
  if (!student) return <div className="page-content"><p>Student not found.</p></div>;

  const strategies = user ? getStudentStrategies(user.id, student.id) : [];

  const patterns: string[] = [];
  if (student.conversations.length >= 3) {
    const days: Record<number, number> = {};
    student.conversations.forEach(c => { const d = new Date(c.date).getDay(); days[d] = (days[d] || 0) + 1; });
    const top = Object.entries(days).sort((a, b) => Number(b[1]) - Number(a[1]))[0];
    if (top && Number(top[1]) >= 2) {
      const dn = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      patterns.push(`${student.initials} has had ${top[1]} conversations logged on ${dn[parseInt(top[0])]}s — this may be a pattern worth exploring.`);
    }
  }
  if (student.conversations.length >= 3) {
    patterns.push(`${student.conversations.length} conversations logged. The app is building a strong picture of this student — referral recommendations will be more specific with each conversation.`);
  }

  const logWin = () => {
    if (!winText.trim() || !user) return;
    const win: Win = { id: generateId(), studentId: student.id, date: Date.now(), description: winText };
    student.wins.push(win);
    if (student.wins.length >= 3) student.trend = 'improving';
    saveStudent(user.id, student);
    setWinText(''); setShowWin(false); setWinSaved(true);
    setTimeout(() => setWinSaved(false), 3000);
  };

  const tryStrategy = (strategy: string) => {
    if (!user) return;
    const attempt: StrategyAttempt = {
      id: generateId(), studentId: student.id, userId: user.id, strategy,
      category: 'top3', dateStarted: Date.now(),
      checkInDate: Date.now() + 3 * 24 * 60 * 60 * 1000,
      status: 'in-progress', conversationTopic: strategy.substring(0, 80)
    };
    saveStrategyAttempt(user.id, attempt);
    setTriedStrategies(prev => [...prev, strategy]);
  };

  const generateSubBrief = async () => {
    setSubLoading(true); setShowSubBrief(true);
    const ctx = student.conversations.map(c => c.response?.summary || c.topic).join('. ');
    const strats = strategies.filter(s => s.status === 'worked').map(s => s.strategy).join(', ');
    try {
      const brief = await getSubBrief(ctx, strats, student.initials, student.grade);
      setSubBrief(brief);
    } catch {
      setSubBrief(`SUBSTITUTE BRIEF — ${student.initials} (${student.grade})\n\nThis student does best with clear expectations and a warm greeting. Check in with them at the start of class.\n\nIf they struggle: Stay calm, lower your voice, offer a simple choice. Avoid public redirection.\n\nThey have real strengths — look for moments to notice them.`);
    }
    setSubLoading(false);
  };

  // Generate a section that was never completed before the student was saved.
  // The teacher may save a student mid-flow (autosave, or hitting Save on the
  // summary before opening every tab), which leaves Strategies / Referral / MTSS
  // etc. blank. This lets them fill any missing section in later, from the profile,
  // using the same API the workup uses — and persists it so it's there next time.
  // (Referral was never persisted at all on older saves, so it's always fillable.)
  const regenerateSection = async (c: any, section: string) => {
    if (!user || !student) return;
    setRegenErr(null); setRegen({ conv: c.id, section }); setRegenStream('');
    const input = c.input || c.topic || '';
    try {
      let value: any;
      if (section === 'brain') value = await streamBrainScience(input, student.initials, student.grade, setRegenStream);
      else if (section === 'strategies') value = await getStrategies(input, student.initials, student.grade);
      else if (section === 'forYou') value = await streamForYou(input, student.initials, student.grade, setRegenStream);
      else if (section === 'refer') value = await streamReferral(input, student.initials, student.grade, setRegenStream);
      else if (section === 'mtss') value = await getMTSSAssessment(input, student.initials, student.grade);
      else return;
      const field = section === 'brain' ? 'brainScience' : section === 'refer' ? 'whenToRefer' : section;
      if (!c.response) c.response = {};
      c.response[field] = value;
      saveStudent(user.id, student);
      setTick(t => t + 1);
    } catch (e) {
      setRegenErr({ conv: c.id, section });
    } finally {
      setRegen(null); setRegenStream('');
    }
  };

  // The "generate this missing section now" card, shown inside a tab whose data
  // is blank. Streams live text for the prose sections while it works.
  const generateBox = (c: any, section: string, label: string) => {
    const busy = regen?.conv === c.id && regen?.section === section;
    const failed = regenErr?.conv === c.id && regenErr?.section === section;
    return (
      <div style={{ background: 'var(--teal-light)', borderRadius: 10, padding: '16px', textAlign: 'center' }}>
        {busy ? (
          <>
            <div className="spinner" />
            <p style={{ color: 'var(--teal)', marginTop: 10, fontWeight: 600 }}>Generating {label}…</p>
            {regenStream && <p style={{ fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', color: 'var(--charcoal)', marginTop: 10, textAlign: 'left' }}>{regenStream}</p>}
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: 'var(--charcoal)', marginBottom: 10, lineHeight: 1.5 }}>
              {failed ? "That didn't come through just now. Give it a moment and try again." : `This section wasn't finished when ${student.initials} was saved. Generate it now — it'll be saved to the profile.`}
            </p>
            <button onClick={() => regenerateSection(c, section)} className="btn-small" disabled={!!regen}>
              {failed ? 'Try again' : `Generate ${label}`}
            </button>
          </>
        )}
      </div>
    );
  };

  const tabs = [['summary', 'Summary'], ['brain', 'Brain Science'], ['strategies', 'Strategies'], ['forYou', 'For You'], ['refer', L.referralTab], ['mtss', L.mtssTab]];

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('students')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Students
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>{student.initials}</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content">
        {/* Header card */}
        <div className="card" style={{ textAlign: 'center', marginBottom: 16 }}>
          <div className="profile-initials" style={{ fontSize: 40 }}>{student.initials}</div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 4 }}>{student.grade}</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12 }}>
            <div style={{ textAlign: 'center' }}><p style={{ fontWeight: 700, fontSize: 20 }}>{student.conversations.length}</p><p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Conversations</p></div>
            <div style={{ textAlign: 'center' }}><p style={{ fontWeight: 700, fontSize: 20 }}>{student.wins.length}</p><p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Wins</p></div>
            <div style={{ textAlign: 'center' }}><p style={{ fontWeight: 700, fontSize: 20 }}>{strategies.length}</p><p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Strategies</p></div>
          </div>
          <div style={{ marginTop: 12 }}>
            <span className={`trend trend-${student.trend}`}>{student.trend === 'improving' ? '↑ Improving' : student.trend === 'stable' ? '→ Stable' : '↓ Needs attention'}</span>
          </div>
        </div>

        {/* Pattern alerts */}
        {patterns.map((p, i) => (
          <div key={i} style={{ background: 'var(--amber-light)', borderLeft: '4px solid var(--amber)', borderRadius: '0 10px 10px 0', padding: '12px 16px', marginBottom: 8 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#b87800', marginBottom: 4 }}>PATTERN DETECTED</p>
            <p style={{ fontSize: 14 }}>{p}</p>
          </div>
        ))}

        {/* Action buttons */}
        <div className="grid-2" style={{ marginBottom: 10 }}>
          <button onClick={() => navigate('talk')} className="btn-primary" style={{ fontSize: 13, padding: '12px' }}>Talk About {student.initials}</button>
          <button onClick={() => setShowWin(true)} className="btn-amber" style={{ fontSize: 13, padding: '12px' }}>Log a Win 🌟</button>
        </div>
        {!L.isParent && (
          <button onClick={generateSubBrief} style={{ width: '100%', background: 'var(--teal-light)', color: 'var(--teal)', padding: '12px', borderRadius: 10, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', marginBottom: 16 }}>
            📋 Generate Substitute Teacher Brief
          </button>
        )}

        {/* Sub Brief */}
        {showSubBrief && (
          <div className="card animate-in" style={{ marginBottom: 16, border: '2px solid var(--teal)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <p style={{ fontWeight: 700, color: 'var(--teal)' }}>📋 Substitute Brief — {student.initials}</p>
              <button onClick={() => setShowSubBrief(false)} style={{ background: 'none', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>
            {subLoading ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}><div className="spinner" /><p style={{ color: 'var(--teal)', marginTop: 12, fontWeight: 600 }}>Generating brief...</p></div>
            ) : (
              <>
                <p style={{ fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap', color: 'var(--charcoal)' }}>{subBrief}</p>
                <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--teal-light)', borderRadius: 8, fontSize: 12, color: 'var(--teal)' }}>
                  🔒 No identifying information — initials only. Safe to share.
                </div>
              </>
            )}
          </div>
        )}

        {winSaved && <div style={{ background: 'var(--green-light)', color: 'var(--green)', padding: '12px 16px', borderRadius: 10, marginBottom: 12, fontWeight: 600, textAlign: 'center' }}>🎉 Win logged!</div>}

        {showWin && (
          <div className="card" style={{ marginBottom: 16 }}>
            <p style={{ fontWeight: 600, marginBottom: 10 }}>What happened? 🌟</p>
            <textarea value={winText} onChange={e => setWinText(e.target.value)} placeholder={`Example: ${student.initials} made it through all transitions today without incident.`} style={{ minHeight: 80 }} />
            <div className="grid-2" style={{ marginTop: 10 }}>
              <button onClick={logWin} className="btn-amber" style={{ fontSize: 13, padding: '10px' }}>Save Win</button>
              <button onClick={() => setShowWin(false)} className="btn-secondary" style={{ fontSize: 13, padding: '10px' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Strategy Tracker with performance scores */}
        {strategies.length > 0 && user && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>📊 Strategy Tracker</p>
            {strategies.map((a: StrategyAttempt) => {
              const score = getStrategyScore(user.id, a.id);
              const todayRated = getDailyRatings(user.id).some(r => r.strategyAttemptId === a.id && r.dateKey === getTodayDateKey());
              const scoreColor = score.pct >= 70 ? 'var(--green)' : score.pct >= 40 ? 'var(--amber)' : score.total > 0 ? 'var(--coral)' : 'var(--teal)';
              const scoreLabel = score.total === 0 ? 'Not yet rated' : score.pct >= 70 ? 'Bread & butter 🔥' : score.pct >= 40 ? 'Inconsistent ⚠️' : 'Not working ❌';
              return (
                <div key={a.id} className="card" style={{ marginBottom: 10, borderLeft: `4px solid ${scoreColor}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, flex: 1, marginRight: 8 }}>{a.strategy.substring(0, 100)}{a.strategy.length > 100 ? '...' : ''}</p>
                  </div>
                  {/* Performance bar */}
                  {score.total > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                        <span style={{ color: scoreColor, fontWeight: 700 }}>{scoreLabel}</span>
                        <span style={{ color: 'var(--text-secondary)' }}>{score.worked}/{score.total} days</span>
                      </div>
                      <div style={{ height: 6, background: '#f0ece6', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${score.pct}%`, background: scoreColor, borderRadius: 3, transition: '0.6s ease' }} />
                      </div>
                    </div>
                  )}
                  {/* Rate today button */}
                  {a.status === 'in-progress' && !todayRated && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <p style={{ fontSize: 11, color: 'var(--text-secondary)', alignSelf: 'center', marginRight: 4 }}>Today:</p>
                      {(['worked', 'partially', 'didnt-work'] as const).map((r) => (
                        <button key={r} onClick={() => {
                          const rating: DailyRatingEntry = { id: Math.random().toString(36).substr(2,9), strategyAttemptId: a.id, studentId: a.studentId, userId: user.id, date: Date.now(), dateKey: getTodayDateKey(), result: r };
                          saveDailyRating(user.id, rating);
                          setRatingKey(k => k + 1);
                        }} style={{ flex: 1, padding: '7px 4px', borderRadius: 8, fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', background: r === 'worked' ? 'var(--green-light)' : r === 'partially' ? 'var(--amber-light)' : 'var(--coral-light)', color: r === 'worked' ? 'var(--green)' : r === 'partially' ? '#b87800' : 'var(--coral)' }}>
                          {r === 'worked' ? '✅ Yes' : r === 'partially' ? '⚠️ Sort of' : '❌ No'}
                        </button>
                      ))}
                    </div>
                  )}
                  {a.status === 'in-progress' && todayRated && (
                    <p style={{ fontSize: 11, color: 'var(--green)', marginTop: 6, fontWeight: 600 }}>✅ Rated for today</p>
                  )}
                  {a.nextStrategy && <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--amber-light)', borderRadius: 6, fontSize: 12, lineHeight: 1.5 }}><strong>Tried next:</strong> {a.nextStrategy.substring(0, 100)}...</div>}
                </div>
              );
            })}
          </div>
        )}

        {/* Wins */}
        {student.wins.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>🌟 Wins</p>
            {student.wins.map(w => (
              <div key={w.id} className="win-card">
                <p style={{ fontSize: 14 }}>{w.description}</p>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>{new Date(w.date).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}

        {/* Conversation History — TAPPABLE */}
        {student.conversations.length > 0 && (
          <div>
            <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>Conversation History</p>
            {[...student.conversations].reverse().map(c => (
              <div key={c.id} style={{ marginBottom: 12 }}>
                {/* Header — always visible, tappable */}
                <div className="card" style={{ cursor: 'pointer', borderBottom: expandedConv === c.id ? '1px solid #e8e3dc' : 'none', borderRadius: expandedConv === c.id ? '12px 12px 0 0' : 12 }}
                  onClick={() => setExpandedConv(expandedConv === c.id ? null : c.id)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{c.topic}</p>
                      <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{new Date(c.date).toLocaleDateString()}</p>
                    </div>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2" width="18" height="18"
                      style={{ transform: expandedConv === c.id ? 'rotate(180deg)' : 'none', transition: '0.2s', flexShrink: 0 }}>
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  </div>
                  {expandedConv !== c.id && c.response?.summary && (
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>{c.response.summary.substring(0, 100)}...</p>
                  )}
                </div>

                {/* Expanded full response */}
                {expandedConv === c.id && c.response && (
                  <div className="animate-in" style={{ background: 'white', borderRadius: '0 0 12px 12px', boxShadow: 'var(--shadow-sm)', padding: '0 16px 16px' }}>
                    {/* Mini tabs */}
                    <div style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingTop: 12, paddingBottom: 8 }}>
                      {tabs.map(([k, l]) => (
                        <button key={k}
                          onClick={() => setExpandedTab(prev => ({ ...prev, [c.id]: k }))}
                          style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', background: (expandedTab[c.id] || 'summary') === k ? 'var(--teal)' : '#f0ece6', color: (expandedTab[c.id] || 'summary') === k ? 'white' : 'var(--text-secondary)' }}>
                          {l}
                        </button>
                      ))}
                    </div>

                    {/* Tab content */}
                    {(() => {
                      const t = expandedTab[c.id] || 'summary';
                      const r = c.response;
                      return (
                        <div>
                          {t === 'summary' && (
                            <div>
                              <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 12 }}>{r.summary}</p>
                              {r.top3?.map((s: string, i: number) => (
                                <div key={i} style={{ marginBottom: 8 }}>
                                  <div className={`strategy-item${i === 0 ? ' highlight' : ''}`} style={{ marginBottom: 0, borderRadius: triedStrategies.includes(s) ? '8px 8px 0 0' : 8 }}>
                                    <span style={{ fontWeight: 700, marginRight: 6 }}>{i + 1}.</span>{s}
                                  </div>
                                  {!triedStrategies.includes(s) ? (
                                    <button onClick={() => tryStrategy(s)} style={{ width: '100%', background: 'var(--teal-light)', color: 'var(--teal)', padding: '7px 14px', borderRadius: '0 0 8px 8px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                                      ✋ I'm trying this today
                                    </button>
                                  ) : (
                                    <div style={{ background: 'var(--green-light)', padding: '7px 14px', borderRadius: '0 0 8px 8px', fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>✅ Check-in saved</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {t === 'brain' && (r.brainScience
                            ? <p style={{ fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{r.brainScience}</p>
                            : generateBox(c, 'brain', 'Brain Science'))}
                          {t === 'strategies' && (!r.strategies
                            ? generateBox(c, 'strategies', 'Strategies')
                            : (
                            <div>
                              {[['academic', '📚 Academic'], ['behavioral', '🎯 Behavioral'], ['relational', '💛 Relational']].map(([key, label]) => (
                                <div key={key} style={{ marginBottom: 16 }}>
                                  <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{label}</p>
                                  {r.strategies?.[key]?.map((s: string, i: number) => (
                                    <div key={i} style={{ marginBottom: 6 }}>
                                      <div className="strategy-item" style={{ marginBottom: 0, borderRadius: triedStrategies.includes(s) ? '8px 8px 0 0' : 8, fontSize: 13 }}>{s}</div>
                                      {!triedStrategies.includes(s) ? (
                                        <button onClick={() => tryStrategy(s)} style={{ width: '100%', background: 'var(--teal-light)', color: 'var(--teal)', padding: '6px 14px', borderRadius: '0 0 8px 8px', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer', textAlign: 'left' }}>✋ Try this</button>
                                      ) : (
                                        <div style={{ background: 'var(--green-light)', padding: '6px 14px', borderRadius: '0 0 8px 8px', fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>✅ Saved</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          ))}
                          {t === 'forYou' && (r.forYou
                            ? <p style={{ fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{r.forYou}</p>
                            : generateBox(c, 'forYou', 'For You'))}
                          {t === 'refer' && (
                            <div>
                              {r.whenToRefer
                                ? <p style={{ fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap', marginBottom: 12 }}>{r.whenToRefer}</p>
                                : <div style={{ marginBottom: 12 }}>{generateBox(c, 'refer', L.referralTab)}</div>}
                              <div style={{ background: 'var(--teal-light)', borderRadius: 10, padding: '12px', border: '1px solid var(--teal)', fontSize: 13 }}>
                                <p style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 4 }}>{L.isParent ? 'Who to talk to' : 'Who to loop in at school'}</p>
                                <p style={{ lineHeight: 1.6 }}>{L.isParent ? "Bring these observations to your child's teacher or school counselor, and your pediatrician or family doctor. They can help you decide together what, if anything, comes next. This tool does not diagnose or give medical advice." : "Decisions about additional support belong to your school's team. Share these observations with your school counselor or student support team."}</p>
                              </div>
                            </div>
                          )}
                          {t === 'mtss' && (r.mtss ? (L.isParent ? (
                            <div>
                              <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 14 }}>What to ask the school for</p>
                              {(r.mtss.askSchool || []).map((q: string, i: number) => (
                                <div key={i} style={{ background: 'var(--bg)', borderRadius: 8, padding: '9px 12px', marginBottom: 6, fontSize: 13, display: 'flex', gap: 8 }}>
                                  <span style={{ color: 'var(--teal)', fontWeight: 700 }}>•</span>{q}
                                </div>
                              ))}
                              {r.mtss.why && <p style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--text-secondary)', marginTop: 8, fontStyle: 'italic' }}>{r.mtss.why}</p>}
                            </div>
                          ) : (
                            <div>
                              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                                <span className={`badge ${r.mtss.tier === 1 ? 'badge-green' : r.mtss.tier === 2 ? 'badge-amber' : 'badge-coral'}`} style={{ fontSize: 14, padding: '6px 16px' }}>
                                  MTSS Tier {r.mtss.tier}
                                </span>
                              </div>
                              <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 10 }}>{r.mtss.explanation}</p>
                              {r.mtss.supports?.map((s: string, i: number) => (
                                <div key={i} className="strategy-item" style={{ fontSize: 13 }}>{s}</div>
                              ))}
                              {r.mtss.citation && (
                                <p style={{ fontSize: 11, lineHeight: 1.6, color: 'var(--text-secondary)', marginTop: 10, fontStyle: 'italic' }}>{r.mtss.citation}</p>
                              )}
                            </div>
                          )) : generateBox(c, 'mtss', L.mtssTab))}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <AiDisclaimer navigate={navigate} />
      </div>
      <NavBar navigate={navigate} active="students" />
    </div>
  );
}
